import { doc, getDoc } from 'firebase/firestore';
import { mergePermissions, type PermissionSet, type RolePermissionDoc } from '@helios/authz';
import { getFirestoreInstance } from './firebase-init';

export interface LoadedPermissions {
  /** Merged permission set across all of the user's roles. */
  permissions: PermissionSet;
  /** Role ids whose Firestore doc was found. */
  loadedRoles: string[];
  /** Role ids whose Firestore doc was missing (treated as no permissions). */
  missingRoles: string[];
}

/**
 * Fetch and merge the Firestore role permission documents for exactly the
 * user's roles — never lists or reads other roles' documents.
 *
 * Fails closed: a missing document contributes no permissions; any read
 * error rejects so the caller can render a No-Access/configuration screen.
 * Path: orgs/{orgId}/rbac_roles/{roleId}.
 */
export async function loadPermissions(orgId: string, roles: string[]): Promise<LoadedPermissions> {
  if (!orgId || roles.length === 0) {
    return { permissions: {}, loadedRoles: [], missingRoles: [...roles] };
  }

  const db = getFirestoreInstance();
  const loadedRoles: string[] = [];
  const missingRoles: string[] = [];
  const sets: PermissionSet[] = [];

  // De-duplicate roles; fetch only the roles the user actually has.
  for (const roleId of [...new Set(roles)]) {
    const ref = doc(db, 'orgs', orgId, 'rbac_roles', roleId);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      missingRoles.push(roleId);
      continue;
    }
    const data = snap.data() as Partial<RolePermissionDoc> | undefined;
    sets.push((data?.permissions ?? {}) as PermissionSet);
    loadedRoles.push(roleId);
  }

  return { permissions: mergePermissions(sets), loadedRoles, missingRoles };
}
